/**
 * iter163 — Phase 2: Permission grouping config.
 *
 * Single source of truth for the grouped view of the Roles & Permissions page.
 * - Keys are the EXACT strings in backend `ALL_PERMS` (see /app/backend/server.py).
 * - Only existing permissions are listed here. Future-only perms (Bucket Daddy,
 *   construction, etc.) are intentionally NOT added until those modules ship.
 *
 * Each permission can be optionally flagged:
 *   - `sensitive: true`  → renders a 🔒 lock icon (pay, PTO, audit log, etc.)
 *   - `danger: true`     → renders a 🛑 red warning badge (delete, refund, etc.)
 *   - `help: '...'`      → optional inline helper text
 *
 * Each group can declare a top-level `warning: '...'` banner that appears
 * under the group title (used for the Time/Payroll sensitive group).
 *
 * Unknown / unmapped permissions returned by the backend catalog fall into a
 * trailing "Other" group so we never silently hide a permission.
 */

export const PERM_GROUPS = [
  {
    key: 'customers',
    label: 'Customers & CRM',
    description: 'Customer records, timeline, and opportunity tools.',
    perms: [
      { key: 'view_customers' },
      { key: 'edit_customers' },
      { key: 'delete_customers', danger: true, help: 'Permanently removes a customer record.' },
      { key: 'merge_customers', help: 'Combines duplicate customer records.' },
      { key: 'invite_customers' },
      { key: 'view_timeline' },
    ],
  },
  {
    key: 'scheduling',
    label: 'Scheduling & Dispatch',
    description: 'Schedule access and job creation/assignment.',
    perms: [
      { key: 'view_schedule' },
      { key: 'view_service_schedule' },
      { key: 'view_office_schedule' },
      { key: 'view_construction_schedule' },
      { key: 'create_jobs' },
      { key: 'edit_jobs' },
      { key: 'assign_jobs' },
    ],
  },
  {
    key: 'billing',
    label: 'Billing, Estimates & Payments',
    description: 'Invoicing, estimating, payment collection, and refunds.',
    perms: [
      { key: 'view_invoices' },
      { key: 'create_invoices' },
      { key: 'edit_invoices' },
      { key: 'delete_invoices', danger: true },
      { key: 'send_invoices' },
      { key: 'mark_invoice_paid' },
      { key: 'generate_payment_links' },
      { key: 'collect_payments' },
      { key: 'apply_discounts', help: 'Apply manual discounts beyond promo codes.' },
      { key: 'apply_promo_codes' },
      { key: 'issue_refunds', danger: true },
      { key: 'grant_credits', help: 'Grant account credits without refunding to card.' },
      { key: 'view_estimates' },
      { key: 'create_estimates' },
      { key: 'edit_estimates' },
      { key: 'delete_estimates', danger: true },
      { key: 'send_estimates' },
    ],
  },
  {
    key: 'employees',
    label: 'Employees / HR Admin',
    description: 'Employee directory and HR actions. Pay/PTO are in the next group.',
    perms: [
      { key: 'view_employees' },
      { key: 'manage_employees', help: 'Legacy umbrella — grants all elevated employee actions below. Prefer the granular permissions.' },
      { key: 'invite_employees' },
      { key: 'onboard_employees' },
      { key: 'edit_employee_profile' },
      { key: 'edit_employee_role_department', sensitive: true, help: 'Changing role rewrites default permissions.' },
      { key: 'view_employee_documents' },
      { key: 'upload_employee_documents' },
      { key: 'reset_employee_password' },
      { key: 'deactivate_employee', danger: true },
      { key: 'archive_employee', danger: true, help: 'Removes from dropdowns but preserves history.' },
    ],
  },
  {
    key: 'time-payroll',
    label: 'Time, PTO & Payroll',
    description: 'Timesheets, time-off, and sensitive payroll fields.',
    warning: 'Sensitive permissions in this group control pay, payroll, and PTO. Do not grant unless explicitly needed — payroll fields are stripped server-side for users without them.',
    perms: [
      { key: 'view_timesheets' },
      { key: 'edit_timesheets' },
      { key: 'approve_time_edits' },
      { key: 'approve_timeoff' },
      { key: 'view_pay_rate', sensitive: true },
      { key: 'edit_pay_rate', sensitive: true, danger: true },
      { key: 'view_pto_balance', sensitive: true },
      { key: 'edit_pto_balance', sensitive: true },
      { key: 'view_payroll', sensitive: true },
      { key: 'edit_payroll', sensitive: true, danger: true },
    ],
  },
  {
    key: 'bonuses',
    label: 'Bonuses',
    description: 'Bonus requests, approvals, and payouts.',
    perms: [
      { key: 'view_bonuses' },
      { key: 'request_bonus' },
      { key: 'create_bonus_for_employee' },
      { key: 'approve_bonus_requests' },
      { key: 'mark_bonus_paid' },
      { key: 'view_bonus_payout_totals', sensitive: true, help: 'Surfaces aggregate payout dollars.' },
    ],
  },
  {
    key: 'inventory',
    label: 'Inventory / Warehouse',
    description: 'Master inventory, stock, and transfers.',
    perms: [
      { key: 'view_inventory' },
      { key: 'edit_inventory' },
    ],
  },
  {
    key: 'vip',
    label: 'VIP / Memberships',
    description: 'VIP plans, settings, and terms.',
    perms: [
      { key: 'view_vip_settings' },
      { key: 'edit_vip_settings' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports & Performance',
    description: 'Financial and operational reporting.',
    perms: [
      { key: 'view_financial', sensitive: true, help: 'Unlocks Tech Performance, Revenue Pipeline, and Marketing reports.' },
    ],
  },
  {
    key: 'system',
    label: 'System / Admin Tools',
    description: 'Company-wide settings, announcements, and audit log.',
    perms: [
      { key: 'company_settings', sensitive: true },
      { key: 'post_announcements' },
      { key: 'approve_uploads' },
      { key: 'view_audit_log', sensitive: true },
    ],
  },
  // Property Management permissions are role-driven for now (no dedicated keys).
  // Danger Zone (/admin/trash, /admin/system) is super_admin-only via route guard,
  // not through permission checkboxes — so it doesn't appear here.
];

/**
 * Build a Map<permKey, {groupKey, meta}> for O(1) lookup.
 * Anything in the backend catalog not present in PERM_GROUPS falls into "Other".
 */
export function buildPermIndex() {
  const idx = new Map();
  for (const g of PERM_GROUPS) {
    for (const p of g.perms) {
      idx.set(p.key, { groupKey: g.key, meta: p });
    }
  }
  return idx;
}

/**
 * Partition the backend's `all_permissions` array into the configured groups
 * plus a trailing "Other" group for any perm not yet mapped. Pure function.
 */
export function partitionPerms(allPermissions) {
  const idx = buildPermIndex();
  const groupsOut = PERM_GROUPS.map((g) => ({
    ...g,
    perms: g.perms.filter((p) => allPermissions.includes(p.key)),
  })).filter((g) => g.perms.length > 0);

  const known = new Set();
  for (const g of groupsOut) {
    for (const p of g.perms) known.add(p.key);
  }
  const orphans = allPermissions.filter((k) => !known.has(k));
  if (orphans.length > 0) {
    groupsOut.push({
      key: 'other',
      label: 'Other',
      description: 'Permissions returned by the server but not yet mapped to a group. Safe to grant — same enforcement as the grouped perms.',
      perms: orphans.map((k) => ({ key: k })),
    });
  }
  return groupsOut;
}
